import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client/react';
import { object, string, array } from 'yup';
import { useMultipartUpload } from '@jwp/ott-hooks-react/src/useMultipartUpload';
import { createURLFromLocation } from '@jwp/ott-ui-react/src/utils/location';
import { useAppConfig } from '@jwp/ott-hooks-react/src/useAppConfig';
import { GET_ALL_DROPDOWNS, GetAllDropdownsData, GetAllDropdownsVariables, DropdownNode } from '@jwp/ott-common/src/graphql/query/video.queries';
import { useUploadStore } from '@jwp/ott-common/src/stores/UploadStore';
import { PATH_VIDEO_PREVIEW } from '@jwp/ott-common/src/paths';
import useForm, { UseFormOnSubmitHandler } from '@jwp/ott-hooks-react/src/useForm';
import AddIcon from '@jwp/ott-theme/assets/icons/plus.svg?react';
import SubscriptionBox from '@jwp/ott-theme/assets/icons/SubscriptionBox';
import { extractMotionThumbnailFromVideo } from '@jwp/ott-common/src/utils/mediaUtils';
import { isValidMediaTagsFormat, parseUniqueMediaTags } from '@jwp/ott-common/src/utils/mediaTags';

import { useTheme } from '../../../../../ThemeContext';
import ErrorIcon from '../../../../theme/assets/icons/cancel_confirm.svg';
import RestartIcon from '../../../../theme/assets/icons/restart.svg';
import Spinner from '../Spinner/Spinner';
import TextField from '../TextField/TextField';
import Dropdown from '../Dropdown/Dropdown';
import Button from '../Button/Button';
import UploadBox from '../UploadBox/UploadBox';
import Icon from '../Icon/Icon';

import styles from './UploadVideoModal.module.scss';

type UploadVideoFormData = {
  title: string;
  description: string;
  genreIds: string[];
  ageRatingId: string;
  tags: string;
};

const UploadVideoModal = () => {
  const { t } = useTranslation('upload');
  const { thumbnailSizeLimit } = useAppConfig();
  const navigate = useNavigate();
  const location = useLocation();

  const {
    file,
    motionThumbnail,
    motionThumbnailUrl,
    staticThumbnail,
    staticThumbnailUrl,
    additionalImages,
    videoValues: storeValues,
    duration,
    previewUrl,
    setFile,
    setMotionThumbnail,
    setStaticThumbnail,
    removeAdditionalImage,
    setVideoValues,
    setDuration,
    setReturnPath,
    mediaId,
    originalAdditionalImages,
    reset: resetStore
  } = useUploadStore();

  const [baseValues, setBaseValues] = useState<UploadVideoFormData | null>(null);

  const isEdit = !!mediaId;

  const { themeConfig, universalStyles } = useTheme();

  const [fileError, setFileError] = useState<string | null>(null);
  const [staticThumbError, setStaticThumbError] = useState<string | null>(null);
  const [motionThumbError, setMotionThumbError] = useState<string | null>(null);

  const { uploadVideo, updateVideoMediaFilesAndMeta, status, error: uploadError, reset: resetUpload } = useMultipartUpload();
  const isUploading = status === 'uploading';
  const [isExtracting, setIsExtracting] = useState(false);

  // Fetch Dropdowns
  const { data: genreData, loading: genreLoading } = useQuery<GetAllDropdownsData, GetAllDropdownsVariables>(GET_ALL_DROPDOWNS, {
    variables: { first: 100, offset: 0, filter: { ddType: { equalTo: 'GENRE' }, ddDeletedAt: { isNull: true } } }
  });

  const { data: ageRatingData, loading: ageRatingLoading } = useQuery<GetAllDropdownsData, GetAllDropdownsVariables>(GET_ALL_DROPDOWNS, {
    variables: { first: 100, offset: 0, filter: { ddType: { equalTo: 'AGE_RATING' }, ddDeletedAt: { isNull: true } } }
  });

  const genres = useMemo(() => genreData?.allDropdowns?.nodes || [], [genreData]);
  const ageRatings = useMemo(() => ageRatingData?.allDropdowns?.nodes || [], [ageRatingData]);

  const handleVideoChange = async (selectedFile: File | null) => {
    setFileError(null);
    if (selectedFile) {
      if (!selectedFile.type.startsWith('video/')) {
        setFileError('upload_video.errors.invalid_file_type');
        setFile(null);
        return;
      }
      if (selectedFile.size > 50 * 1024 * 1024 * 1024) {
        setFileError('upload_video.errors.file_size');
        setFile(null);
        return;
      }

      // 1. Get duration
      const url = URL.createObjectURL(selectedFile);
      const vid = document.createElement('video');
      vid.preload = 'metadata';
      vid.onloadedmetadata = () => {
        const d = Number.isFinite(vid.duration) ? vid.duration : null;
        if (d !== null) {
          setDuration(Math.floor(d * 1000));
        }
        URL.revokeObjectURL(url);
      };
      setDuration(null);
      vid.src = url;

      // 2. Auto-extract motion thumbnail for preview
      setIsExtracting(true);
      try {
        const motionBlob = await extractMotionThumbnailFromVideo(selectedFile);
        const ext = motionBlob.type.includes('mp4') ? 'mp4' : 'webm';
        const nameWithoutExt = selectedFile.name.substring(0, selectedFile.name.lastIndexOf('.')) || selectedFile.name;
        const motionFile = new File([motionBlob], `${nameWithoutExt}_motion.${ext}`, { type: motionBlob.type || 'video/webm' });
        
        // Ensure preview URL is generated clearly
        const extractedMotionUrl = URL.createObjectURL(motionFile);
        setMotionThumbnail(motionFile, extractedMotionUrl);
      } catch (err) {
        console.error('Auto-extraction failed:', err);
      } finally {
        setIsExtracting(false);
      }
    } else {
      setDuration(null);
    }
    setFile(selectedFile);
  };

  const handleStaticThumbnailChange = (f: File | null) => {
    setStaticThumbError(null);
    if (f) {
      if (!f.type.startsWith('image/')) {
        setStaticThumbError(t('thumbnail_picker.errors.invalid_file_type'));
        return;
      }
      if (f.size > thumbnailSizeLimit * 1024 * 1024) {
        setStaticThumbError(t('thumbnail_picker.errors.file_size', { size: thumbnailSizeLimit }));
        return;
      }
    }
    setStaticThumbnail(f, f ? URL.createObjectURL(f) : null);
  };

  const handleMotionThumbnailChange = (f: File | null) => {
    setMotionThumbError(null);
    if (f) {
      const isValidVideo = f.type.startsWith('video/');
      const isValidGif = f.type === 'image/gif';
      if (!isValidVideo && !isValidGif) {
        setMotionThumbError(t('upload_video.errors.motion_thumb_invalid_type'));
        return;
      }
      if (f.size > thumbnailSizeLimit * 1024 * 1024) {
        setMotionThumbError(t('thumbnail_picker.errors.file_size', { size: thumbnailSizeLimit }));
        return;
      }
    }
    setMotionThumbnail(f, f ? URL.createObjectURL(f) : null);
  };

  const schema = useMemo(() => {
    const requiredMessage = t('this_field_is_required', { defaultValue: 'This field is required' });
    const invalidTagsMessage = t('upload_video.errors.invalid_tags', {
      defaultValue: 'Invalid tags format. Tags must start with # and be separated by commas.',
    });
    return object({
      title: string()
        .trim()
        .required(requiredMessage)
        .max(50, t('upload_video.errors.maxLength', { defaultValue: 'Max length exceeded', count: 50 })),
      description: string()
        .trim()
        .max(500, t('upload_video.errors.maxLength', { defaultValue: 'Max length exceeded', count: 500 }))
        .default(''),
      genreIds: array().of(string().required()).min(1, requiredMessage).required(),
      ageRatingId: string().required(requiredMessage),
      tags: string()
        .trim()
        .required(requiredMessage)
        .test('is-valid-tags', invalidTagsMessage, (value) => {
          if (!value) {
            return true;
          }
          return isValidMediaTagsFormat(value);
        }),
    });
  }, [t]);

  const onSubmitHandler: UseFormOnSubmitHandler<UploadVideoFormData> = async (formData, { setSubmitting, validate, setValidationSchemaError }) => {
    setFileError(null);
    setStaticThumbError(null);

    if (!isEdit && !file) {
      setFileError('upload_video.errors.select_video');
    }

    if (!staticThumbnail && !staticThumbnailUrl) {
      setStaticThumbError(t('upload_video.errors.static_thumb_required'));
    }

    if (!validate(schema) || (!isEdit && !file) || (!staticThumbnail && !staticThumbnailUrl)) {
      setSubmitting(false);
      setValidationSchemaError(true);
      return;
    }

    try {
      const mediaTags = parseUniqueMediaTags(formData.tags);

      if (isEdit && mediaId) {
        const newAdditionalImages = additionalImages.filter(img => img.file && img.file.size > 0);
        const existingAdditionalImages = additionalImages
          .filter(img => !img.file || img.file.size === 0)
          .map(img => ({
            maAdditionalImagesS3KeyUrl: img.thumbnailUrl.replace(window.location.origin, '').replace(/^https?:\/\/[^/]+/, '').replace(/^\//, ''),
            maAdditionalImagesLabelId: img.labelId,
            maAdditionalImagesLabelName: img.labelName,
            maAdditionalImagesLabelValue: img.labelValue,
            maIsThumbnail: false
          }));

        const existingStaticThumbKey = staticThumbnailUrl && !staticThumbnailUrl.startsWith('blob:') 
          ? staticThumbnailUrl.replace(window.location.origin, '').replace(/^https?:\/\/[^/]+/, '').replace(/^\//, '') 
          : null;
        const existingMotionThumbKey = motionThumbnailUrl && !motionThumbnailUrl.startsWith('blob:') 
          ? motionThumbnailUrl.replace(window.location.origin, '').replace(/^https?:\/\/[^/]+/, '').replace(/^\//, '') 
          : null;

        await updateVideoMediaFilesAndMeta(
          mediaId,
          {
            maTitle: formData.title,
            maDescription: formData.description,
            genreIds: formData.genreIds,
            ageRatingId: formData.ageRatingId,
            maTags: mediaTags,
            maDuration: duration ? String(duration) : undefined,
          },
          motionThumbnail,
          staticThumbnail,
          newAdditionalImages,
          existingStaticThumbKey,
          existingMotionThumbKey,
          existingAdditionalImages
        );
      } else if (file) {
        await uploadVideo(
          file,
          {
            maTitle: formData.title,
            maDescription: formData.description,
            maType: 'VIDEOS',
            maTags: mediaTags,
            genreIds: formData.genreIds,
            ageRatingId: formData.ageRatingId,
            maDuration: duration ? String(duration) : undefined,
          },
          motionThumbnail || undefined,
          staticThumbnail || undefined,
          additionalImages
        );
      }
    } catch (error: any) {
      console.error('Upload failed:', error);
    }

    setSubmitting(false);
  };

  const { handleSubmit, handleChange, handleBlur, values, errors, validationSchemaError, touched, setValue } = useForm<UploadVideoFormData>({
    initialValues: storeValues,
    validationSchema: schema as any,
    validateOnBlur: true,
    onSubmit: onSubmitHandler,
  });

  useEffect(() => {
    setVideoValues(values);
  }, [values, setVideoValues]);

  useEffect(() => {
    if (isEdit && !baseValues && storeValues.title) {
      setBaseValues(storeValues as UploadVideoFormData);
    }
  }, [isEdit, baseValues, storeValues]);

  useEffect(() => {
    if (status === 'completed') {
      const wasEdit = !!mediaId;
      resetStore();
      resetUpload();
      const nextView = wasEdit ? 'edit-success' : 'upload-success';
      navigate(createURLFromLocation(location, { u: nextView, message: 'video' }));
    }
  }, [status, navigate, location, resetStore, resetUpload, mediaId]);

  const handleAddAdditional = () => {
    navigate(createURLFromLocation(location, { u: 'add-additional-image', id: null }));
  };

  const isDirty = useMemo(() => {
    if (!isEdit || !baseValues) { return false; }

    const sanitizeTags = (t: string) => t.split(',').map(tag => tag.trim()).filter(Boolean).sort((a, b) => a.localeCompare(b)).join(',');
    const currentTags = sanitizeTags(values.tags);
    const originalTags = sanitizeTags(baseValues.tags);

    return (
      values.title.trim() !== baseValues.title.trim() ||
      (values.description || '').trim() !== (baseValues.description || '').trim() ||
      JSON.stringify([...values.genreIds].sort((a, b) => a.localeCompare(b))) !== JSON.stringify([...baseValues.genreIds].sort((a, b) => a.localeCompare(b))) ||
      values.ageRatingId !== baseValues.ageRatingId ||
      currentTags !== originalTags ||
      !!staticThumbnail || 
      !!motionThumbnail ||
      (originalAdditionalImages !== null && additionalImages.length !== originalAdditionalImages.length) ||
      additionalImages.some((img, idx) => {
        const orig = originalAdditionalImages?.[idx];
        return img.labelId !== orig?.labelId || img.thumbnailUrl !== orig?.thumbnailUrl || (img.file?.size ?? 0) > 0;
      })
    );
  }, [isEdit, values, baseValues, staticThumbnail, motionThumbnail, additionalImages, originalAdditionalImages]);

  const isFormValid = schema.isValidSync(values) && (!!staticThumbnail || !!staticThumbnailUrl) && (isEdit ? isDirty : !!file);
  const isUploadingState = isUploading;

  const submitButtonLabel = useMemo(() => {
    if (isExtracting) { return t('upload_video.form.extracting_label', { defaultValue: 'Extracting...' }); }
    if (isUploadingState) { return t('upload_video.form.submitting_button'); }
    if (isEdit) { return t('upload_video.form.save_changes', { defaultValue: 'Save Changes' }); }
    return t('upload_video.form.submit_button');
  }, [isExtracting, isUploadingState, isEdit, t]);

  return (
    <div className={styles.container}>
      <h2 className={styles.title}>{isEdit ? t('upload_video.edit_title', { defaultValue: 'Edit Video' }) : t('upload_video.title')}</h2>

      <div className={styles.content}>
        <div className={styles.leftColumn}>
          <div className={styles.section}>
            <span className={styles.label}>{t('upload_video.form.video_label')}</span>
            <UploadBox
              selectedFile={file}
              onFileSelect={handleVideoChange}
              hint={t('upload_video.form.video_hint')}
              error={!!fileError}
              accept="video/*"
              height={250}
              type="video"
              duration={duration}
              previewUrl={previewUrl || undefined}
              onPreview={() => {
                const backPath = `${location.pathname}${location.search}`;
                setReturnPath(backPath);
                navigate(`${PATH_VIDEO_PREVIEW}?type=video&returnPath=${encodeURIComponent(backPath)}`);
              }}
              disabled={isUploadingState || isExtracting || isEdit}
              readOnly={!!file || isEdit}
            />
            {fileError ? <div className={styles.errorText}>{t(fileError)}</div> : null}
          </div>

          <div className={styles.section}>
            <span className={styles.label}>{t('upload_video.form.thumbnails_label', { defaultValue: 'Thumbnails' })}</span>
            <div className={styles.thumbnailsRow}>
              <div className={styles.thumbItem}>
                <span className={styles.subLabel}>{t('upload_video.form.motion_thumbnail_label')}</span>
                <UploadBox
                  selectedFile={motionThumbnail}
                  onFileSelect={handleMotionThumbnailChange}
                  onDelete={() => {
                    setMotionThumbError(null);
                    setMotionThumbnail(null, null);
                  }}
                  showControlsOnHover
                  autoPlay={true}
                  loop={true}
                  muted={true}
                  playsInline={true}
                  hint={t('upload_video.form.motion_thumbnail_hint', { size: thumbnailSizeLimit })}
                  error={!!motionThumbError}
                  accept="video/*, .gif"
                  height={119}
                  previewUrl={motionThumbnailUrl || undefined}
                  type="video"
                  disabled={isUploadingState || isExtracting}
                />
                {motionThumbError ? <div className={styles.errorText}>{motionThumbError}</div> : null}
                <div className={styles.hintText}>{t('upload_video.form.motion_thumbnail_description')}</div>
              </div>
              <div className={styles.thumbItem}>
                <span className={styles.subLabel}>
                  {t('upload_video.form.static_thumbnail_label')} <span className={styles.required}>*</span>
                </span>
                <UploadBox
                  selectedFile={staticThumbnail}
                  onFileSelect={handleStaticThumbnailChange}
                  onDelete={() => {
                    setStaticThumbError(null);
                    setStaticThumbnail(null, null);
                  }}
                  showControlsOnHover
                  hint={t('upload_video.form.static_thumbnail_hint', { size: thumbnailSizeLimit })}
                  error={!!staticThumbError}
                  accept="image/*"
                  height={119}
                  className={styles.thumbArea}
                  previewUrl={staticThumbnailUrl || undefined}
                  poster={staticThumbnailUrl || undefined}
                  type="image"
                  disabled={isUploadingState || isExtracting}
                />
                {staticThumbError ? <div className={styles.errorText}>{staticThumbError}</div> : null}
                <div className={styles.hintText}>{t('upload_video.form.static_thumbnail_description')}</div>
              </div>
            </div>
          </div>

          <div className={styles.section}>
            <div className={styles.sectionHeader}>
              <span className={styles.label}>{t('upload_video.form.additional_images_label')}</span>
              {additionalImages.length > 0 && (
                <button className={styles.addLink} onClick={handleAddAdditional} disabled={isUploadingState || isExtracting}>
                  <Icon icon={AddIcon} />
                  <span>{t('upload_video.add_additional_image.add_image_label', { defaultValue: 'Add Image' })}</span>
                </button>
              )}
            </div>

            {additionalImages.length === 0 ? (
              <div className={styles.emptyContainer}>
                <SubscriptionBox color={themeConfig?.primary || '#e31152'} bg={universalStyles.ternaryButton.backgroundColor} />
                <p className={styles.emptyText}>{t('upload_video.form.no_additional_images', { defaultValue: 'No Images have been added' })}</p>
                <Button
                  label={t('upload_video.add_additional_image.add_image_label')}
                  onClick={handleAddAdditional}
                  variant="mediaOverlay"
                  className={styles.addBtnSmall}
                  disabled={isUploadingState || isExtracting}
                />
              </div>
            ) : (
              <div className={styles.additionalGallery}>
                {additionalImages.map((img, index) => (
                  <UploadBox
                    key={index}
                    selectedFile={img.file}
                    previewUrl={img.thumbnailUrl}
                    onFileSelect={() => {}}
                    onEdit={() => navigate(createURLFromLocation(location, { u: 'add-additional-image', id: String(index) }))}
                    onDelete={() => removeAdditionalImage(index)}
                    label={img.labelName}
                    showControlsOnHover
                    height={119}
                  />
                ))}
              </div>
            )}
          </div>
        </div>

        <form id="upload-video-form" onSubmit={handleSubmit} className={styles.rightColumn} noValidate>
          <TextField
            name="title"
            label={t('upload_video.form.title_label')}
            value={values.title}
            onChange={handleChange}
            onBlur={handleBlur}
            error={(validationSchemaError || touched.title !== undefined) && !!errors.title}
            helperText={validationSchemaError || touched.title !== undefined ? (errors.title as string) : ''}
            required
            maxLength={50}
            placeholder={t('upload_video.form.title_placeholder')}
            disabled={isUploadingState || isExtracting}
          />

          <TextField
            name="description"
            label={t('upload_video.form.description_label')}
            value={values.description}
            onChange={handleChange}
            onBlur={handleBlur}
            error={(validationSchemaError || touched.description !== undefined) && !!errors.description}
            helperText={validationSchemaError || touched.description !== undefined ? (errors.description as string) : ''}
            multiline
            maxLength={500}
            showOptionalLabel={false}
            placeholder={t('upload_video.form.description_placeholder')}
            disabled={isUploadingState || isExtracting}
          />

          <Dropdown
            name="genreIds"
            label={t('upload_video.form.genre_label')}
            value={values.genreIds}
            onChange={(e) => setValue('genreIds', e.target.value as unknown as string[])}
            onBlur={handleBlur as any}
            error={(validationSchemaError || touched.genreIds !== undefined) && !!errors.genreIds}
            helperText={validationSchemaError || touched.genreIds !== undefined ? (errors.genreIds as unknown as string) : ''}
            required
            defaultLabel={genreLoading ? t('common:loading') : t('upload_video.form.genre_placeholder')}
            options={genres.map((g: DropdownNode) => ({ value: g.ddId, label: g.ddLabel }))}
            fullWidth
            disabled={isUploadingState || isExtracting}
            multiSelect
          />

          <Dropdown
            name="ageRatingId"
            label={t('upload_video.form.age_rating_label')}
            value={values.ageRatingId}
            onChange={handleChange as any}
            onBlur={handleBlur as any}
            error={(validationSchemaError || touched.ageRatingId !== undefined) && !!errors.ageRatingId}
            helperText={validationSchemaError || touched.ageRatingId !== undefined ? (errors.ageRatingId as unknown as string) : ''}
            required
            defaultLabel={ageRatingLoading ? t('common:loading') : t('upload_video.form.age_rating_placeholder')}
            options={ageRatings.map((a: DropdownNode) => ({ value: a.ddId, label: a.ddLabel }))}
            fullWidth
            disabled={isUploadingState || isExtracting}
          />

          <TextField
            name="tags"
            label={t('upload_video.form.tag_label')}
            value={values.tags}
            onChange={handleChange}
            onBlur={handleBlur}
            error={(validationSchemaError || touched.tags !== undefined) && !!errors.tags}
            helperText={validationSchemaError || touched.tags !== undefined ? (errors.tags as string) : ''}
            required
            placeholder={t('upload_video.form.tag_placeholder')}
            disabled={isUploadingState || isExtracting}
          />
        </form>
      </div>

      <div className={styles.submitContainer}>
        <Button
          form="upload-video-form"
          type="submit"
          label={submitButtonLabel}
          variant="contained"
          color="primary"
          disabled={isUploadingState || isExtracting || !isFormValid}
          className={styles.submitBtn}
        />
      </div>

      {isUploadingState && (
        <div className={styles.loadingOverlay}>
          <Spinner size="medium" />
          <div className={styles.uploadStatus}>{t('upload_reel:upload_box.uploading', { defaultValue: 'Uploading...' })}</div>
        </div>
      )}

      {status === 'failed' && (
        <div className={styles.loadingOverlay}>
          <img src={ErrorIcon} alt="error" />
          <div className={styles.uploadStatus} style={{ color: 'var(--alert-error)' }}>
            {uploadError || t('upload_video.errors.upload_failed')}
          </div>
          <Button
            startIcon={<img src={RestartIcon} alt="restart" width={22} height={22} />}
            label={t('common.retry', { defaultValue: 'Try Again' })}
            onClick={resetUpload}
            variant="mediaOverlay"
            color="secondary"
          />
        </div>
      )}
    </div>
  );
};

export default UploadVideoModal;
